function cartDrawerPrefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Scrolls nested overflow regions (drawer list, drawer column, etc.) so the
 * focused control stays visible, with smooth motion when moving e.g. between
 * .drawer__top and .drawer__footer.
 */
function scrollDrawerFocusedElementIntoView(cartDrawerRoot, focused) {
  if (!(focused instanceof HTMLElement)) return;
  const smooth = !cartDrawerPrefersReducedMotion();
  const behavior = smooth ? 'smooth' : 'auto';
  const inSideRecommendations = Boolean(focused.closest('aside.cart-drawer-side'));
  const pad = inSideRecommendations
    ? Math.max(16, Math.round(0.2 * Math.min(window.innerHeight, window.innerWidth)))
    : 16;

  const scrollables = [];
  let node = focused.parentElement;
  while (node && cartDrawerRoot.contains(node) && node !== cartDrawerRoot) {
    const st = getComputedStyle(node);
    const oy = st.overflowY;
    const ox = st.overflowX;
    const canY =
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
      node.scrollHeight > node.clientHeight;
    const canX =
      (ox === 'auto' || ox === 'scroll') && node.scrollWidth > node.clientWidth;
    if (canY || canX) scrollables.push(node);
    node = node.parentElement;
  }

  const scrollStep = (index) => {
    if (index >= scrollables.length) return;
    const sp = scrollables[index];
    const elRect = focused.getBoundingClientRect();
    const spRect = sp.getBoundingClientRect();
    const st = getComputedStyle(sp);
    const canY =
      (st.overflowY === 'auto' || st.overflowY === 'scroll' || st.overflowY === 'overlay') &&
      sp.scrollHeight > sp.clientHeight;
    const canX =
      (st.overflowX === 'auto' || st.overflowX === 'scroll') &&
      sp.scrollWidth > sp.clientWidth;

    let dy = 0;
    let dx = 0;
    if (canY) {
      if (elRect.top < spRect.top + pad) dy = elRect.top - spRect.top - pad;
      else if (elRect.bottom > spRect.bottom - pad) dy = elRect.bottom - spRect.bottom + pad;
    }
    if (canX) {
      if (elRect.left < spRect.left + pad) dx = elRect.left - spRect.left - pad;
      else if (elRect.right > spRect.right - pad) dx = elRect.right - spRect.right + pad;
    }

    if (dy !== 0 || dx !== 0) {
      sp.scrollTo({
        top: sp.scrollTop + dy,
        left: sp.scrollLeft + dx,
        behavior,
      });
    }

    if (index + 1 < scrollables.length) {
      requestAnimationFrame(() => scrollStep(index + 1));
    }
  };

  if (scrollables.length) {
    scrollStep(0);
  } else {
    focused.scrollIntoView({ behavior, block: 'nearest', inline: 'nearest' });
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.addEventListener('focusin', (event) => {
      if (!this.classList.contains('active')) return;
      const target = event.target;
      if (!(target instanceof HTMLElement) || !this.contains(target)) return;
      requestAnimationFrame(() => {
        const focused = document.activeElement;
        if (!(focused instanceof HTMLElement) || !this.contains(focused)) return;
        scrollDrawerFocusedElementIntoView(this, focused);
        if (focused.closest('aside.cart-drawer-side')) {
          const behavior = cartDrawerPrefersReducedMotion() ? 'auto' : 'smooth';
          requestAnimationFrame(() => {
            if (document.activeElement !== focused) return;
            focused.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior });
          });
        }
      });
    });

    // Listen for cart updates to reapply focus trap
    document.addEventListener('update-cart-render', () => {
      if (this.classList.contains('active')) {
        // The re-rendered drawer HTML ships with the cross-sell aside inert again
        this.removeInert();
        setTimeout(() => {
          this.reapplyFocusTrap();
        }, 250);
      }
    });
    
    // Prevent focus from escaping during DOM updates
    document.addEventListener('focusout', (event) => {
      if (this.classList.contains('active') && !this.contains(event.relatedTarget)) {
        // Focus is leaving the drawer, prevent it
        setTimeout(() => {
          if (this.classList.contains('active') && !this.contains(document.activeElement)) {
            const fallbackElement = this.querySelector('[data-link-cart-empty]') || this.querySelector('.drawer__close');
            if (fallbackElement) {
              fallbackElement.focus({ preventScroll: true });
            }
          }
        }, 10);
      }
    });
  }

  connectedCallback() {
    this.removeAttribute('hidden')
  }

  // focus trapping is handled by global trapFocus utility on open()

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return; // some store aren't e-commerce
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      this.applySidePanelLinkTabindex();
      // Check if cart is empty by looking at the cart-drawer element class or cart state
      const isEmptyByClass = this.classList.contains('is-empty');
      const isEmptyByElements = !this.querySelector('.cart-item') || this.querySelectorAll('.cart-item').length === 0;
      const isEmpty = isEmptyByClass || isEmptyByElements;
      const containerToTrapFocusOn = isEmpty
        ? this.querySelector('.drawer__inner') || this
        : document.getElementById('CartDrawer');
      const focusElement = isEmpty 
        ? this.querySelector('[data-link-cart-empty]') || this.querySelector('.drawer__close')
        : this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
    this.removeInert();

    document.body.classList.add('u-overflow-hidden');
  }

  close() {
    const body = this.querySelector('body');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('u-overflow-hidden');
    this.addInert();
  }

  removeInert() {
    this.querySelectorAll('aside.cart-drawer-side[inert]').forEach(side => {
      side.inert = false;
    });
    this.applySidePanelLinkTabindex();
  }

  /**
   * Réapplique tabindex="-1" sur les liens image des recommandations (évite double lien produit).
   * Le navigateur ou d'autres scripts peuvent supprimer l'attribut.
   */
  applySidePanelLinkTabindex() {
    this.querySelectorAll('aside.cart-drawer-side a.cart-drawer-side__recommended-product-link').forEach((link) => {
      link.setAttribute('tabindex', '-1');
    });
  }

  addInert() {
    this.querySelectorAll('aside.cart-drawer-side[inert]').forEach(side => {
      side.inert = true;
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const focusSnap =
      typeof window.captureCartFocus === 'function' ? window.captureCartFocus(document.activeElement) : null;

    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections[section.id];
      if (html == null) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner === null) return;
      sectionElement.innerHTML = inner;
    });

    document.dispatchEvent(new CustomEvent('update-cart-render'));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // If opened via Add to Cart, remember the current focused element to restore on close
      if (!this.activeElement) this.setActiveElement(document.activeElement);
      this.open();
      // Reapply focus trap after content is rendered
      setTimeout(() => {
        this.reapplyFocusTrap(focusSnap);
      }, 100);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (html == null || html === '') return null;
    const el = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      },
      {
        id: 'announcement-bar',
        section: document.getElementById('announcement-bar')?.dataset.id,
        selector: '.js-contents-announcement',
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
    // Force focus trap to stay active during cart transitions
  forceFocusTrapDuringTransition() {
    if (this.classList.contains('active')) {
      const currentFocusedElement = document.activeElement;
      
      // Prevent focus from leaving the drawer during DOM updates
      const observer = new MutationObserver(() => {
        if (this.classList.contains('active') && !this.contains(document.activeElement)) {
          // Focus has left the drawer, bring it back
          const fallbackElement = this.querySelector('[data-link-cart-empty]') ||
                                 this.querySelector('.drawer__close');
          if (fallbackElement) {
            fallbackElement.focus({ preventScroll: true });
          }
        }
      });
      
      observer.observe(this, { childList: true, subtree: true });
      
      // Stop observing after 1 second
      setTimeout(() => {
        observer.disconnect();
        this.reapplyFocusTrap();
      }, 1000);
    }
  }

  // Method to reapply focus trap after cart updates
  reapplyFocusTrap(focusSnapshot) {
    if (this.classList.contains('active')) {
      // Check if cart is empty by looking at the cart-drawer element class or cart state
      const isEmptyByClass = this.classList.contains('is-empty');
      const isEmptyByElements = !this.querySelector('.cart-item') || this.querySelectorAll('.cart-item').length === 0;
      const isEmpty = isEmptyByClass || isEmptyByElements;
      
      // Add a delay and verify elements exist before applying trap
      setTimeout(() => {
        // Re-check elements exist after DOM update
        let containerToTrapFocusOn;
        let focusElement = null;

        if (
          focusSnapshot &&
          typeof window.restoreCartFocus === 'function'
        ) {
          const restored = window.restoreCartFocus(focusSnapshot);
          if (restored instanceof HTMLElement && this.contains(restored)) {
            focusElement = restored;
          }
        }
        
        if (isEmpty) {
          // When cart is empty, focus should be trapped in the entire drawer
          containerToTrapFocusOn = this.querySelector('.drawer__inner') || this;
          // Try to focus on the "start buying" link, fallback to close button
          if (!focusElement) {
            focusElement = this.querySelector('[data-link-cart-empty]') || this.querySelector('.drawer__close');
          }
          
          // Debug: verify empty state elements exist
          if (!focusElement) {
            console.warn('Cart drawer: No focusable element found for empty state');
            focusElement = this.querySelector('.drawer__close');
          }
        } else {
          // When cart has items, trap focus in the main cart drawer
          containerToTrapFocusOn = document.getElementById('CartDrawer') || this;
          // Focus on the first cart item or close button
          if (!focusElement) {
            focusElement = this.querySelector('.cart-item__name') || 
                          this.querySelector('.cart-item') || 
                          this.querySelector('.drawer__close');
          }
        }
        
        // Remove existing trap and immediately reapply with new elements
        if (containerToTrapFocusOn && focusElement) {
          this.applySidePanelLinkTabindex();
          removeTrapFocus();
          trapFocus(containerToTrapFocusOn, focusElement);
        } else {
          console.warn('Cart drawer: Failed to find elements for focus trap', {
            container: !!containerToTrapFocusOn,
            focusElement: !!focusElement,
            isEmpty
          });
        }
      }, 100);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer-container'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'announcement-bar',
        section: document.getElementById('announcement-bar')?.dataset.id,
        selector: '.js-contents-announcement',
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
